import { ofetch } from 'ofetch';
import { withAuthRetry } from './auth-retry';
import { correlationIdOf, mapStatusToError } from './errors';
import { applyStandardHeaders } from './headers';
import type { $Fetch } from 'ofetch';

export interface ApiClientConfig {
  /** From VITE_API_URL. */
  baseURL: string;
  /** Surface-specific base path, e.g. '/marketplace'. */
  basePath?: string;
  /** Returns the current access token, or null if unauthenticated. */
  getToken: () => string | null;
  /** Current UI language for Accept-Language. */
  getLanguage: () => string;
  /** Single-flight token refresh. Resolves true if a new token is available. */
  refreshToken: () => Promise<boolean>;
  /** Called when refresh fails — app redirects to its login surface. */
  onAuthFailure: () => void;
}

/**
 * Builds the one ofetch client for a web surface, per the accepted web stack
 * baseline. Composes header application, user-safe error mapping, and 401
 * auth-retry from small focused modules. Never logs tokens, OTP, card tokens,
 * PayLink/Wallet secrets or sensitive personal data.
 */
export function createApiClient(config: ApiClientConfig): $Fetch {
  const base = ofetch.create({
    baseURL: `${config.baseURL}${config.basePath ?? ''}`,
    retry: 0,

    onRequest({ options }) {
      const headers = new Headers(options.headers);
      applyStandardHeaders(headers, {
        method: options.method,
        token: config.getToken(),
        language: config.getLanguage(),
      });
      options.headers = headers;
    },

    // No `onResponse` here on purpose: a request-level hook replaces an instance hook of the
    // same name, and the back-office change-password reads a response header with one.
    onResponseError({ response }) {
      const status = response.status;
      // The backend's error envelope is `{code, message, correlation_id, timestamp,
      // details}`. `details` is forwarded because some domain refusals carry the
      // content the caller has to act on — point publication lists every unmet rule
      // there. `message` is deliberately not forwarded: the UI phrases its own text.
      const body =
        response._data && typeof response._data === 'object'
          ? (response._data as {
              code?: string;
              details?: Record<string, unknown>;
              correlation_id?: unknown;
            })
          : undefined;
      throw mapStatusToError(
        status,
        body?.code,
        body?.details,
        correlationIdOf(body?.correlation_id) ?? response.headers.get('X-Correlation-Id'),
      );
    },
  });

  return withAuthRetry(base as never, {
    refreshToken: config.refreshToken,
    onAuthFailure: config.onAuthFailure,
  });
}

/** Attach a stable idempotency key so retries of one command reuse it. */
export function withIdempotencyKey(key: string): { headers: Record<string, string> } {
  return { headers: { 'Idempotency-Key': key } };
}
